// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { StatusToken } from '../../../utils/entityHelpers';
import type { AssetLiveState } from '../../../utils/assetLiveState';
import type { FoldedLoads } from '../../../utils/powerFlowCollapse';
import type { MeterReading } from '../../../utils/powerFlowCaptions';
import type { PowerFlowModel } from '../../../utils/powerFlowModel';
import type { PowerFlowNode } from '../../../utils/powerFlowLayout';
import type { PowerFlowLiveState } from '../../../utils/powerFlowLive';
import { EMPTY_LIVE } from '../../../utils/powerFlowLive';
import { annotateAsset, busSublabel } from '../../../utils/powerFlowCaptions';
import { useStageTransform } from '../../../hooks/useStageTransform';
import { STATUS_LABELS } from '../../../utils/statusPresentation';
import { GraphZoomControls } from './GraphZoomControls';
import { SldBus } from './SldBus';
import { SldEdgeLayer } from './SldEdgeLayer';
import { paintConductors } from './conductors/conductorPaint';
import { lockFlowPhase, nodeFeeds } from './conductors/flowPulse';
import { SldEnclosureLayer } from './SldEnclosure';
import { SldLoadGroup } from './SldLoadGroup';
import { SldSheetChrome } from './SldSheetChrome';
import { SldNode } from './SldNode';
import type { DownstreamStatus } from './primitives/DownstreamMark';
import { alarmLine, alarmsFor, type AlarmsByAsset } from '../../../utils/powerFlowAlarms';
import { BreakerAlarmPopover, type BreakerAlarmTarget } from './conductors/BreakerAlarmPopover';

export interface SldStageProps {
  model: PowerFlowModel;
  live: AssetLiveState;
  /**
   * What the readings say about how the plant is running: energization, flow,
   * standby, loading, N+1. Rebuilt every poll, like `live`, and painted onto
   * the same geometry. Omitted draws structure and status only.
   */
  powerLive?: PowerFlowLiveState;
  /** When false, inferred paint (standby, bypass, flow, enclosures, captions) is withheld. */
  showInferred?: boolean;
  /** Epoch ms, for the age captions. Injected so tests are deterministic. */
  now?: number;
  /** Asset id → display label and type, for the chip and its accessible name. */
  describeAsset: (_id: string) => {
    short: string;
    full: string;
    type: string;
    /** Nameplate parts; `[]` renders an em-dash, `undefined` renders no line. */
    nameplate?: (string | number | null | undefined)[];
  };
  /** Display type for a load group's members, e.g. `IT Rack`. */
  describeGroupType: (_entityType: string) => string;
  /** Asset id → its open alarms (`powerFlowAlarms`), for hover lines and the alarm popover. */
  alarmsOf?: AlarmsByAsset;
  selectedId: string | null;
  onSelect: (_id: string | null) => void;
  /** Fans a collapsed load group back out into individual chips. */
  onExpandGroup: (_groupId: string) => void;
  /** Re-frames the stage when it changes, unless the user has panned. */
  fitKey: string;
}

/**
 * The pan/zoom surface: one absolutely positioned chip or bar per asset, plus a
 * single SVG conductor layer, inside a transformed stage.
 *
 * `useStageTransform` is carried over from Asset Topology unchanged — the pan,
 * zoom, fit and viewport-in-a-ref machinery was expensive to get right and none
 * of it cares what is being drawn. That hook is also why a telemetry poll
 * cannot reset the viewport.
 *
 * **Accessibility follows the shape of the drawing, not the pixels.** A one-line
 * is a chain, so the diagram is one `role="grid"` whose rows are electrical
 * ranks: left and right move along a rank, up and down move between them to the
 * horizontally nearest asset, which is the same movement as tracing a conductor
 * with a finger. One roving tab stop per rank keeps a 60-asset site at four tab
 * stops rather than sixty. `role="application"` suppresses browse mode, so
 * everything here has to be reachable from the keyboard — and the page also
 * always offers the Asset Directory as a non-canvas route to the same data.
 */
export const SldStage = ({
  model,
  live,
  powerLive = EMPTY_LIVE,
  showInferred = true,
  now,
  describeAsset,
  describeGroupType,
  alarmsOf,
  selectedId,
  onSelect,
  onExpandGroup,
  fitKey,
}: SldStageProps) => {
  const { graph, inference, display, layout, conductors } = model;
  const { columns, bands } = model.sheets.sheet;

  /**
   * What a box, bar or badge is standing over after the fold — `12 IT Racks` —
   * and the health of what it hides. The fold is a display transform, so the
   * folded members still have a status in `live`; this is where it gets back
   * onto the drawing.
   */
  const foldedOf = useCallback(
    (id: string): FoldedSummary | undefined => {
      const folded = display.foldedLoadsOf.get(id);
      if (!folded || folded.length === 0) return undefined;
      const note = folded
        .map(entry => {
          const label = describeGroupType(entry.entityType);
          return `${entry.count} ${label}${entry.count === 1 || /s$/i.test(label) ? '' : 's'}`;
        })
        .join(', ');
      const memberIds = folded.flatMap(entry => entry.memberIds ?? []);
      const status = worstStatusOf(memberIds, live.statusOf);
      const faultCount = memberIds.filter(member => live.statusOf.get(member) === 'fault').length;
      const warnCount = memberIds.filter(member => live.statusOf.get(member) === 'warn').length;
      const counts = [
        faultCount > 0 ? `${faultCount} alarming` : '',
        warnCount > 0 ? `${warnCount} degraded` : '',
      ].filter(Boolean);
      const spoken = `${note} folded${counts.length > 0 ? `, ${counts.join(', ')}` : ''}`;
      return {
        note,
        spoken,
        downstream: { status, faultCount, warnCount, label: spoken },
      };
    },
    [display.foldedLoadsOf, describeGroupType, live.statusOf]
  );

  /** Drawn id → worst folded status, for the column roll-up. */
  const foldedStatusOf = useMemo(() => {
    const index = new Map<string, StatusToken>();
    for (const [id, folded] of Array.from(display.foldedLoadsOf.entries())) {
      const memberIds = folded.flatMap((entry: FoldedLoads) => entry.memberIds ?? []);
      if (memberIds.length > 0) index.set(id, worstStatusOf(memberIds, live.statusOf));
    }
    return index;
  }, [display.foldedLoadsOf, live.statusOf]);
  const clock = now ?? Date.now();

  const { viewportRef, stageRef, viewportProps, zoomIn, zoomOut, fit, didDrag, zoomPercent } =
    useStageTransform({
      contentWidth: layout.width,
      contentHeight: layout.height,
      // Clear of the block picker floating over the canvas's top-left
      // (`.sld-canvas__toolbar`: 0.75rem offset + a 2rem control + its padding
      // and border, with a little air).
      // ponytail: a constant tied to that CSS; measure the toolbar if it grows.
      insetTop: 56,
      fitKey,
    });

  /** Which asset in each rank is focusable. Exactly one per rank. */
  const rovingCell = useRef<Map<number, string>>(new Map());

  const handleSelect = useCallback(
    (id: string) => {
      if (didDrag()) return;
      onSelect(id === selectedId ? null : id);
    },
    [didDrag, onSelect, selectedId]
  );

  /** Assets in each rank, left to right. */
  const nodesByRank = useMemo(() => {
    const index = new Map<number, PowerFlowNode[]>();
    for (const node of layout.nodes) {
      const bucket = index.get(node.rank);
      if (bucket) bucket.push(node);
      else index.set(node.rank, [node]);
    }
    for (const bucket of Array.from(index.values())) bucket.sort((a, b) => a.x - b.x);
    return index;
  }, [layout.nodes]);

  /**
   * The selection, everything that feeds it up to the sources, and everything
   * it feeds down to the last load — the whole electrical path through the
   * clicked device, whatever sits along the way.
   *
   * Descendants hidden behind a collapsed load group light the **group badge**,
   * not its members: the fold is a display choice and a selection must not undo
   * it. `null` means no selection, which the conductor layer reads as "dim
   * nothing" — with nothing selected the whole plant is equally legible.
   */
  const connectedIds = useMemo(() => {
    if (!selectedId) return null;
    const ids = new Set<string>([selectedId]);
    for (const direction of ['in', 'out'] as const) {
      const queue = [selectedId];
      while (queue.length) {
        const current = queue.shift() as string;
        for (const next of graph.verticesById.get(current)?.[direction] ?? []) {
          if (ids.has(next)) continue;
          ids.add(next);
          queue.push(next);
        }
      }
    }
    for (const id of Array.from(ids)) {
      const group = display.groupOf.get(id);
      if (group) ids.add(group);
    }
    return ids;
  }, [graph.verticesById, display.groupOf, selectedId]);

  const highlightedIds = useMemo(() => {
    if (!connectedIds) return new Set<string>();
    const ids = new Set(connectedIds);
    ids.delete(selectedId as string);
    return ids;
  }, [connectedIds, selectedId]);

  const focusNode = useCallback((id: string) => {
    document.querySelector<HTMLElement>(`[data-sld-node="${cssEscape(id)}"]`)?.focus();
  }, []);

  const onStageKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const nodeId = (event.target as HTMLElement | null)?.dataset?.sldNode;
      if (!nodeId) return;
      const node = layout.nodesById.get(nodeId);
      if (!node) return;

      const rank = nodesByRank.get(node.rank) ?? [];
      const index = rank.findIndex(other => other.id === nodeId);

      const moveTo = (target: PowerFlowNode | undefined) => {
        if (!target) return;
        rovingCell.current.set(target.rank, target.id);
        focusNode(target.id);
        event.preventDefault();
        event.stopPropagation();
      };

      switch (event.key) {
        case 'ArrowRight':
          return moveTo(rank[index + 1]);
        case 'ArrowLeft':
          return moveTo(rank[index - 1]);
        case 'ArrowDown':
          return moveTo(nearestInRank(nodesByRank.get(node.rank + 1), node));
        case 'ArrowUp':
          return moveTo(nearestInRank(nodesByRank.get(node.rank - 1), node));
        case 'Home':
          return moveTo(rank[0]);
        case 'End':
          return moveTo(rank[rank.length - 1]);
        default:
          return undefined;
      }
    },
    [focusNode, layout.nodesById, nodesByRank]
  );

  const isFocusable = useCallback(
    (node: PowerFlowNode): boolean => {
      const roving = rovingCell.current.get(node.rank);
      if (roving) return roving === node.id;
      return (nodesByRank.get(node.rank) ?? [])[0]?.id === node.id;
    },
    [nodesByRank]
  );

  const statusFor = useCallback(
    (id: string): StatusToken => live.statusOf.get(id) ?? 'idle',
    [live]
  );

  /**
   * Each conductor's live paint, derived once per poll and handed to the edge
   * layer, so the dash a run draws and the dot it pulses read one verdict.
   */
  const paintOf = useMemo(
    () => paintConductors(conductors, powerLive, inference, showInferred),
    [conductors, powerLive, inference, showInferred]
  );

  /**
   * Which assets power is reaching right now, and when the flow dash reaches
   * each one — the status dot pulses on that beat (`flowPulse.ts`).
   */
  const feedOf = useMemo(
    () => nodeFeeds(conductors, conductor => paintOf.get(conductor.id)?.flowing === true),
    [conductors, paintOf]
  );

  useLayoutEffect(() => {
    lockFlowPhase(stageRef.current);
  }, [feedOf, stageRef]);
  const shortNameOf = useCallback((id: string) => describeAsset(id).short, [describeAsset]);
  const alarmLinesOf = useCallback((id: string) => alarmsOf?.get(id)?.map(alarmLine), [alarmsOf]);
  // The breaker whose ALARM mark was activated; its popover sits on the stage
  // so it pans and zooms with the drawing.
  const [alarmTarget, setAlarmTarget] = useState<BreakerAlarmTarget | null>(null);
  const closeAlarmPopover = useCallback(() => setAlarmTarget(null), []);

  return (
    <div className="sld-stage-wrap">
      <div
        ref={viewportRef}
        className="sld-viewport"
        role="application"
        aria-roledescription="Single-line diagram"
        aria-label="Site power distribution"
        aria-describedby="sld-stage-help"
        onDragStart={event => event.preventDefault()}
        {...viewportProps}
        onKeyDown={event => {
          onStageKeyDown(event);
          if (!event.defaultPrevented) viewportProps.onKeyDown(event);
        }}
      >
        <p id="sld-stage-help" className="sld-sr-only">
          The site&apos;s electrical distribution, sources at the top and load at the bottom, each
          row one step further from the source. Tab moves between rows, left and right move along a
          row, up and down follow a conductor to the next row, Enter opens an asset. Plus and minus
          zoom, 0 fits the diagram to the window.
        </p>

        <div
          ref={stageRef}
          className="sld-stage"
          style={{ width: layout.width, height: layout.height }}
        >
          {columns.length > 0 ? (
            <SldSheetChrome
              columns={columns}
              bands={bands}
              layout={layout}
              statusOf={live.statusOf}
              foldedStatusOf={foldedStatusOf}
              powerLive={powerLive}
              labelOf={id => describeAsset(id).short}
              showInferred={showInferred}
            />
          ) : (
            showInferred && (
              <SldEnclosureLayer blocks={inference.blocks} nodesById={layout.nodesById} />
            )
          )}

          <SldEdgeLayer
            conductors={conductors}
            width={layout.width}
            height={layout.height}
            statusOf={live.statusOf}
            connectedIds={connectedIds}
            live={powerLive}
            inference={inference}
            showInferred={showInferred}
            labelOf={shortNameOf}
            alarmsOf={alarmsOf}
            onAlarmClick={setAlarmTarget}
            paint={paintOf}
          />

          {alarmTarget && (
            <BreakerAlarmPopover
              target={alarmTarget}
              alarms={alarmsFor(alarmsOf, alarmTarget.ids)}
              nameOf={shortNameOf}
              onClose={closeAlarmPopover}
            />
          )}

          {/*
            The grid wrappers carry semantics only — the chips inside are
            absolutely positioned in stage coordinates, so these must not
            establish a box that shifts them (`display: contents`).
          */}
          <div
            role="grid"
            aria-label="Power distribution assets by electrical depth"
            aria-rowcount={layout.rows.length}
            aria-colcount={maxRankSize(nodesByRank)}
            className="sld-grid"
          >
            {layout.rows.map(row => {
              const rankNodes = nodesByRank.get(row.rank) ?? [];
              return (
                <div
                  role="row"
                  key={row.rank}
                  aria-rowindex={row.rank + 1}
                  aria-label={rankLabel(row.rank, layout.rows.length)}
                  className="sld-grid"
                >
                  {rankNodes.map(node => {
                    if (node.shape === 'group' && node.group) {
                      const group = node.group;
                      const typeLabel = describeGroupType(group.entityType);
                      const folded = foldedOf(node.id);
                      const groupStatus = worstStatusOf(
                        group.memberIds,
                        live.statusOf,
                        folded?.downstream.status
                      );
                      const depth = rankLabel(node.rank, layout.rows.length);
                      return (
                        <SldLoadGroup
                          key={node.id}
                          node={node}
                          status={groupStatus}
                          typeLabel={typeLabel}
                          note={folded?.note}
                          accessibleName={`${group.memberIds.length} ${typeLabel} assets, ${depth}, ${STATUS_LABELS[groupStatus]}${folded ? `, feeding ${folded.spoken}` : ''}, collapsed. Activate to show each one.`}
                          highlighted={highlightedIds.has(node.id)}
                          focusable={isFocusable(node)}
                          onExpand={onExpandGroup}
                        />
                      );
                    }

                    const info = describeAsset(node.id);
                    const status = statusFor(node.id);
                    const downstream = describeDownstream(model, node.id, describeAsset);
                    const annotation = annotateAsset(node.vertex, powerLive, inference, {
                      now: clock,
                      nameOf: id => describeAsset(id).short,
                      showInferred,
                    });
                    const meterText = annotation.meter
                      ? [annotation.meter.volts, annotation.meter.power, annotation.meter.current]
                          .filter(Boolean)
                          .join(', ')
                      : '';
                    const folded = foldedOf(node.id);
                    const standingOver = folded?.note;
                    const accessibleName = [
                      info.full,
                      info.type,
                      rankLabel(node.rank, layout.rows.length),
                      STATUS_LABELS[status],
                      folded ? `feeding ${folded.spoken}` : '',
                      annotation.state
                        ? annotation.stateInferred && annotation.stateRule
                          ? `${annotation.state}, ${annotation.stateRule}`
                          : annotation.state
                        : '',
                      annotation.values.join(', '),
                      meterText,
                      annotation.badge
                        ? annotation.badge.rule
                          ? `${annotation.badge.text}, ${annotation.badge.rule}`
                          : annotation.badge.text
                        : '',
                      downstream ? `Feeds ${downstream}` : '',
                      ...(alarmLinesOf(node.id) ?? []),
                    ]
                      .filter(Boolean)
                      .join(', ');

                    const shared = {
                      node,
                      status,
                      label: info.short,
                      accessibleName,
                      stale: annotation.stale,
                      badge: annotation.badge,
                      downstream: folded?.downstream,
                      alarmLines: alarmLinesOf(node.id),
                      feed: feedOf.get(node.id) ?? 'quiet',
                      selected: selectedId === node.id,
                      highlighted: highlightedIds.has(node.id),
                      focusable: isFocusable(node),
                      onSelect: handleSelect,
                    };

                    return node.shape === 'bus' ? (
                      <SldBus
                        key={node.id}
                        {...shared}
                        meter={annotation.meter}
                        sublabel={
                          [
                            showInferred ? busSublabel(node.vertex, inference) : undefined,
                            standingOver,
                          ]
                            .filter(Boolean)
                            .join(' · ') || undefined
                        }
                      />
                    ) : (
                      <SldNode
                        key={node.id}
                        {...shared}
                        state={annotation.state}
                        stateInferred={annotation.stateInferred}
                        run={annotation.run}
                        stateRule={annotation.stateRule}
                        values={boxValues(node.vertex.role, annotation, standingOver)}
                      />
                    );
                  })}
                  {rankNodes.length === 0 && <span className="sld-sr-only">Empty</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <GraphZoomControls
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onFit={() => fit()}
        zoomPercent={zoomPercent}
      />
    </div>
  );
};

/** What a drawn asset stands over after the fold, and how it is doing. */
interface FoldedSummary {
  /** `14 IT Racks` — the sub-label. */
  note: string;
  /** `14 IT Racks folded, 1 alarming` — the accessible phrase. */
  spoken: string;
  downstream: DownstreamStatus;
}

/**
 * The figures a box prints, at most two, with the folded count first.
 *
 * A bus that lost its taps to the fold — an RPP whose racks are a count — is
 * drawn with the box renderer, but `annotateAsset` still put its readings in
 * the bus meter and emptied `values`. Rather than lose the kW and A, the
 * meter becomes the box's figures: power then current, or volts when neither
 * is published. The count outranks them, and the box never prints a third
 * line — the accessible name carries everything.
 */
export const boxValues = (
  role: string,
  annotation: { values: string[]; meter?: MeterReading },
  standingOver: string | undefined
): string[] => {
  let figures = annotation.values;
  if (role === 'bus' && annotation.meter) {
    const meter = annotation.meter;
    const primary = [meter.power, meter.current].filter((v): v is string => Boolean(v));
    figures = primary.length > 0 ? primary : meter.volts ? [meter.volts] : [];
  }
  return [standingOver, ...figures].filter((v): v is string => Boolean(v)).slice(0, 2);
};

/**
 * The most serious status among a group's members, and optionally one more
 * status folded in — what those members feed.
 *
 * A collapsed badge must not be able to hide a fault: the whole reason the
 * group is drawable is that its members are electrically interchangeable, and
 * the one thing that distinguishes them is their health.
 */
const worstStatusOf = (
  ids: readonly string[],
  statusOf: Map<string, StatusToken>,
  also?: StatusToken
): StatusToken => {
  let worst: StatusToken = also ?? 'idle';
  if (worst === 'fault') return 'fault';
  for (const id of ids) {
    const status = statusOf.get(id) ?? 'idle';
    if (status === 'fault') return 'fault';
    if (status === 'warn') worst = 'warn';
    else if (status === 'ok' && worst !== 'warn') worst = 'ok';
  }
  return worst;
};

/** The asset in `candidates` whose centre is nearest `from`'s. */
const nearestInRank = (
  candidates: PowerFlowNode[] | undefined,
  from: PowerFlowNode
): PowerFlowNode | undefined => {
  if (!candidates || candidates.length === 0) return undefined;
  const centre = from.x + from.w / 2;
  return candidates.reduce((best, node) =>
    Math.abs(node.x + node.w / 2 - centre) < Math.abs(best.x + best.w / 2 - centre) ? node : best
  );
};

const maxRankSize = (nodesByRank: Map<number, PowerFlowNode[]>): number =>
  Array.from(nodesByRank.values()).reduce((max, nodes) => Math.max(max, nodes.length), 0);

/**
 * What a rank is called out loud.
 *
 * Deliberately not named after equipment types — the rank is derived from the
 * `feeds` chain, so calling rank 2 "UPS" would be a claim the layout does not
 * make and would be wrong on the first site that puts a transformer there.
 */
const rankLabel = (rank: number, total: number): string => {
  if (rank === 0) return 'sources';
  if (rank === total - 1) return 'final distribution';
  return `distribution level ${rank}`;
};

/** Comma-joined labels of what this asset feeds, for the accessible name. */
const describeDownstream = (
  model: PowerFlowModel,
  id: string,
  describeAsset: (_id: string) => { short: string }
): string => {
  const vertex = model.graph.verticesById.get(id);
  if (!vertex) return '';
  const MAX = 4;
  const named = vertex.out.slice(0, MAX).map(other => describeAsset(other).short);
  const remainder = vertex.out.length - named.length;
  return remainder > 0 ? `${named.join(', ')} and ${remainder} more` : named.join(', ');
};

/**
 * Minimal CSS.escape for attribute selectors. Entity ids come from an upstream
 * API, so they can contain characters that are meaningful in a selector.
 */
const cssEscape = (value: string): string =>
  typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(value)
    : value.replace(/["\\]/g, '\\$&');
